import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
    FaBook,
    FaChevronLeft,
    FaChevronRight,
    FaExternalLinkAlt,
} from 'react-icons/fa';
import { coverImageUrl, withLegacyImageFallback } from '../../helper/mediaUrl';
import { journalPublishUrl } from '../../helper/journalUrl';
import { WEBSITE_SETTINGS_DEFAULTS } from '../../helper/websiteMeta';

const AUTOPLAY_MS = 5000;

const JournalSlider = ({
    institutionName = WEBSITE_SETTINGS_DEFAULTS.institutionName,
    systemTitle = WEBSITE_SETTINGS_DEFAULTS.systemTitle,
}) => {
    const [journals, setJournals] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [paused, setPaused] = useState(false);

    useEffect(() => {
        let cancelled = false;

        axios
            .get('api/journal/public')
            .then((res) => {
                if (!cancelled) {
                    setJournals(Array.isArray(res.data) ? res.data : []);
                }
            })
            .catch(() => {
                if (!cancelled) setJournals([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    const goTo = useCallback(
        (index) => {
            if (!journals.length) return;
            const next = ((index % journals.length) + journals.length) % journals.length;
            setActiveIndex(next);
        },
        [journals.length],
    );

    const goNext = useCallback(() => {
        goTo(activeIndex + 1);
    }, [activeIndex, goTo]);

    const goPrev = useCallback(() => {
        goTo(activeIndex - 1);
    }, [activeIndex, goTo]);

    useEffect(() => {
        if (journals.length <= 1 || paused) return undefined;

        const timer = window.setInterval(goNext, AUTOPLAY_MS);
        return () => window.clearInterval(timer);
    }, [journals.length, goNext, paused]);

    if (loading) {
        return (
            <div className="journal-slider journal-slider--empty">
                <p className="text-sm text-muted">Memuat daftar jurnal…</p>
            </div>
        );
    }

    if (!journals.length) {
        return (
            <div className="journal-slider journal-slider--empty card p-10 text-center">
                <FaBook className="mx-auto mb-3 h-8 w-8 text-primary/40 dark:text-primary-on-dark/40" />
                <p className="text-sm text-muted">Belum ada jurnal yang dipublikasikan.</p>
            </div>
        );
    }

    const activeJournal = journals[activeIndex];
    const hasMultiple = journals.length > 1;

    return (
        <div
            className="journal-slider"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
        >
            <div className="journal-slider__shell card overflow-hidden">
                {hasMultiple ? (
                    <>
                        <button
                            type="button"
                            className="journal-slider__arrow journal-slider__arrow--prev"
                            onClick={goPrev}
                            aria-label="Jurnal sebelumnya"
                        >
                            <FaChevronLeft className="h-3.5 w-3.5" />
                        </button>
                        <button
                            type="button"
                            className="journal-slider__arrow journal-slider__arrow--next"
                            onClick={goNext}
                            aria-label="Jurnal berikutnya"
                        >
                            <FaChevronRight className="h-3.5 w-3.5" />
                        </button>
                    </>
                ) : null}

                <div className="journal-slider__viewport">
                    <div
                        className="journal-slider__track"
                        style={{ transform: `translateX(-${activeIndex * 100}%)` }}
                    >
                        {journals.map((journal) => {
                            const publishUrl = journalPublishUrl(journal.journal_publish);

                            return (
                                <article
                                    key={journal.journal_id}
                                    className="journal-slider__slide"
                                >
                                    <div className="journal-slider__inner">
                                        <div className="journal-slider__cover-frame">
                                            {journal.cover ? (
                                                <img
                                                    src={coverImageUrl(journal.cover)}
                                                    alt={journal.journal_name || 'Cover jurnal'}
                                                    className="journal-slider__cover"
                                                    onError={(e) =>
                                                        withLegacyImageFallback(e, journal.cover)
                                                    }
                                                />
                                            ) : (
                                                <div className="journal-slider__cover-placeholder">
                                                    <FaBook className="h-12 w-12 text-primary/25 dark:text-primary-on-dark/25" />
                                                </div>
                                            )}
                                        </div>

                                        <div className="journal-slider__content">
                                            <p className="journal-slider__eyebrow">Jurnal</p>
                                            <h3 className="journal-slider__title">
                                                {journal.journal_name || 'Tanpa nama'}
                                            </h3>
                                            <p className="journal-slider__desc">
                                                Terdaftar dalam sistem {systemTitle}{' '}
                                                {institutionName}.
                                            </p>
                                            {publishUrl ? (
                                                <a
                                                    href={publishUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn-primary journal-slider__cta inline-flex"
                                                >
                                                    Kunjungi jurnal
                                                    <FaExternalLinkAlt className="h-3 w-3" />
                                                </a>
                                            ) : null}
                                        </div>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                </div>
            </div>

            {hasMultiple ? (
                <div className="journal-slider__footer">
                    <p className="text-xs tabular-nums text-muted">
                        {activeIndex + 1} / {journals.length}
                    </p>
                    <div className="journal-slider__dots" role="tablist" aria-label="Pilih jurnal">
                        {journals.map((journal, index) => (
                            <button
                                key={journal.journal_id}
                                type="button"
                                role="tab"
                                aria-selected={index === activeIndex}
                                aria-label={journal.journal_name || `Jurnal ${index + 1}`}
                                className={`journal-slider__dot${
                                    index === activeIndex ? ' journal-slider__dot--active' : ''
                                }`}
                                onClick={() => goTo(index)}
                            />
                        ))}
                    </div>
                </div>
            ) : null}

            <p className="sr-only" aria-live="polite">
                {activeJournal?.journal_name}
            </p>
        </div>
    );
};

export default JournalSlider;
